import type { Dispatch, ReactNode, SetStateAction } from "react";

export const gradientPositions = ["top", "left", "none"] as const;
export type GradientPosition = (typeof gradientPositions)[number];

export type HeroV2Content = {
  backgroundType: "image" | "video";
  removeVideoRoundedCorners?: boolean;
  logo?: string;
  logoHeight?: number;
  logoWidth?: number;
  logoAlt?: string;
  mobileVariant: "transparentContentOnTop" | "contentAtTheBottomWithBackground";
  content: {
    visibility?: string;
    tagLine?: string;
    title?: string;
    titleLogged?: ReactNode;
    subheadline?: string;
    subheadlineLogged?: string;
    isH1TagDisabled?: boolean;
    text?: string;
    textLogged?: string;
    cta?: HeroV2CtaType[];
    discounts?: HeroV2DiscountType[];
    cards?: HeroCardType[];
  };
  theme?: "dark" | "light" | "inherit";
  desktopImage?: HeroImageType;
  mediumImage?: OptionalHeroImageType;
  smallImage?: HeroImageType;
  videoFileReference?: string;
  videoFileType?: string;
  tabletVideoFileReference?: string;
  tabletVideoFileType?: string;
  mobileVideoFileReference?: string;
  mobileVideoFileType?: string;
  displayScrollBar: boolean;
  logoReference?: string;
  withBreadcrumbs?: boolean;
  clipped: boolean;
  legalDisclaimer?: string;
  isViewportHeight?: boolean;
  isContainerWidth?: boolean;
  /**
   * @deprecated: use `gradientPosition` instead
   */
  hasGradientLeft?: boolean;
  gradientPosition?: GradientPosition;
  gradientPositionTablet?: GradientPosition;
  gradientPositionMobile?: GradientPosition;
  isHeroCardsShown?: boolean;
  renderImage?: (props: { src: string; alt: string }) => JSX.Element;
  imageClassName?: string;
  videoClassName?: string;
  renderBackgroundImage?: (
    props: Pick<HeroV2Content, "desktopImage" | "mediumImage" | "smallImage" | "imageClassName">
  ) => JSX.Element | null;
  setIsVideoLoaded?: Dispatch<SetStateAction<boolean>>;
  isFirstElement?: boolean;
};

export type HeroV2Props = {
  data: HeroV2Content[];
  isContainerWidth?: boolean;
  isViewportHeight?: boolean;
  withLoader?: boolean;
  autoplayEnabled?: boolean;
  autoplayDelayInSeconds?: number;
};

export type HeroContentProps = {
  logo?: string;
  logoHeight?: number;
  logoWidth?: number;
  logoAlt?: string;
  tagLine?: string;
  title?: ReactNode;
  subheadline?: string;
  isH1TagDisabled?: boolean;
  text?: string;
  legalDisclaimer?: string;
  discounts?: HeroV2DiscountType[];
  cta?: HeroV2CtaType[];
  displayScrollBar: boolean;
  isViewportHeight?: boolean;
  isContainerWidth?: boolean;
  gradientPosition?: GradientPosition;
  gradientPositionTablet?: GradientPosition;
  gradientPositionMobile?: GradientPosition;
  isLoading?: boolean;
  isHeroCardsShown?: boolean;
  renderImage?: (props: { src: string; alt: string }) => JSX.Element;
  hasCards?: boolean;
  logoReference?: string;
  withBreadcrumbs?: boolean;
  heroContentClassName?: string;
  theme?: "dark" | "light" | "inherit";
};

export type HeroBackgroundProps = Pick<
  HeroV2Content,
  | "backgroundType"
  | "desktopImage"
  | "mediumImage"
  | "smallImage"
  | "videoFileReference"
  | "videoFileType"
  | "tabletVideoFileReference"
  | "tabletVideoFileType"
  | "mobileVideoFileReference"
  | "mobileVideoFileType"
  | "isHeroCardsShown"
  | "removeVideoRoundedCorners"
  | "imageClassName"
  | "renderBackgroundImage"
  | "videoClassName"
  | "setIsVideoLoaded"
  | "isFirstElement"
>;

export type HeroImageType = {
  src: string;
  title: string;
  alt: string;
};

export type OptionalHeroImageType = Partial<HeroImageType>;

export type HeroV2DiscountType = {
  currentPrice: string;
  originalPrice: string;
  packInformation?: string;
  priceDisclaimer?: string;
};

export type HeroV2CtaType = {
  additionalBehavior: null | "onlyForLoggedIn" | "onlyForNotLoggedIn" | "always";
  link: string;
  label: string;
  theme: "light" | "dark";
  layout: "chevron" | "full" | "border";
  tracking: string;
  target: "_blank" | "";
};

export type HeroCardType = {
  image: HeroImageType;
  link: string;
  label: string;
  altText?: string;
  openIn?: "_blank" | "";
};

export type HeroCardsType = {
  cards: HeroCardType[];
  hasDiscounts?: boolean;
};

export type HeroV2LoyaltyCtaType = Omit<HeroV2CtaType, "additionalBehavior"> & {
  additionalBehavior: null | "onlyForLoggedIn" | "onlyForNotLoggedIn" | "always" | "onlyForRestrictedUsers";
};

export type HeroV2LoyaltyContent = Omit<HeroV2Content["content"], "cta" | "title"> & {
  cta?: HeroV2LoyaltyCtaType[];
  title?: string;
  restrictedContent?: boolean;
  restrictedTitle?: string;
  restrictedText?: string;
};
